import apiClient from './apiClient';

const MAX_FILE_SIZE_BYTES = 1024 * 1024; // 1MB

export const MAX_FILE_SIZE_MB = 1;

/** @returns {Promise<{ enabled: boolean, dailyLimitPerCandidate: number, maxDaysPerWeekPerCandidate: number, rateLimitZone?: string }>} */
export async function fetchResumeRateLimitInfo() {
  const { data } = await apiClient.get('public/resume/rate-limit');
  return data;
}

/** Types allowed without auth (mirror backend ResumePublicController). */
const ANONYMOUS_EVAL_TYPES = new Set(['resume-screening-contractor-evaluate', 'resume-jd-evaluate']);

/**
 * @param {File} file
 * @param {string} type - evaluate-contractor | evaluate-fulltime | improve-with-jd | resume-jd-evaluate | resume-screening-contractor-evaluate | resume-screening-contractor-rewrite | resume-screening-fulltime-evaluate | resume-screening-fulltime-rewrite
 * @param {string} [jd] - job description when type is improve-with-jd
 */
export const evaluateResume = async (file, type = 'evaluate-fulltime', jd = null) => {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(`File size must be under ${MAX_FILE_SIZE_MB}MB.`);
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  if (jd) formData.append('jd', jd);

  const path =
    typeof type === 'string' && ANONYMOUS_EVAL_TYPES.has(type.trim())
      ? 'public/resume/evaluate'
      : 'resume/evaluate';

  try {
    const response = await apiClient.post(path, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      const msg = error.response.data?.error || error.response.data?.message || 'Failed to evaluate resume';
      throw new Error(msg);
    } else if (error.request) {
      throw new Error('Unable to connect to server. Please make sure the backend is running.');
    } else {
      throw new Error(error.message || 'An error occurred while processing your request');
    }
  }
};

/**
 * Phase 2 rewrite: send stored resume content and Phase 1 evaluation result (no file upload).
 * @param {string} resumeContent - Extracted resume text from Phase 1 response
 * @param {object} evaluationResult - Phase 1 screening result (screeningResult from evaluate response)
 * @param {string} [promptType] - Optional: 'resume-screening-contractor-rewrite' (default) or 'resume-screening-fulltime-rewrite'
 * @returns {Promise<{ success: boolean, evaluation?: string, usage?: object, error?: string }>}
 */
export const rewriteResume = async (resumeContent, evaluationResult, promptType = 'resume-screening-contractor-rewrite') => {
  if (!resumeContent || !resumeContent.trim()) {
    throw new Error('Resume content is required. Run Phase 1 first.');
  }
  if (!evaluationResult) {
    throw new Error('Phase 1 evaluation result is required. Run Phase 1 first.');
  }

  try {
    const response = await apiClient.post(
      'resume/rewrite',
      { resumeContent, evaluationResult, promptType },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      const msg = error.response.data?.error || error.response.data?.message || 'Rewrite failed.';
      throw new Error(msg);
    } else if (error.request) {
      throw new Error('Unable to connect to server. Please make sure the backend is running.');
    } else {
      throw new Error(error.message || 'An error occurred.');
    }
  }
};

/**
 * Export plain-text resume to formatted .docx (backend uses POI for layout).
 * @param {string} content - Rewritten resume text
 * @returns {Promise<{ blob: Blob, filename: string }>}
 */
export const exportResumeToDocx = async (content) => {
  if (!content || !content.trim()) throw new Error('No content to export.');
  try {
    const response = await apiClient.post('resume/export-docx', { content }, { responseType: 'blob' });
    const blob = response.data;
    const disposition = response.headers['content-disposition'];
    let filename = `resume-rewritten-${new Date().toISOString().slice(0, 10)}.docx`;
    if (disposition && /filename[*]?=(.+)/i.test(disposition)) {
      const match = disposition.match(/filename[*]?=(?:UTF-8'')?["']?([^"'\s]+)["']?/i);
      if (match) filename = match[1].trim();
    }
    return { blob, filename };
  } catch (error) {
    if (error.response?.status === 400) throw new Error('Content cannot be empty.');
    if (error.request) throw new Error('Unable to connect to server.');
    throw new Error(error.message || 'Export failed.');
  }
};

/**
 * Export evaluation to PDF (JD match JSON, resume screening JSON, or plain text).
 * Uses the same public endpoint as JD match; filename comes from Content-Disposition.
 * @param {string} content - Raw evaluation (JSON string or plain text)
 * @returns {Promise<{ blob: Blob, filename: string }>}
 */
export const exportJdEvaluationToPdf = async (content) => {
  if (!content || !content.trim()) throw new Error('No evaluation to export.');
  try {
    const response = await apiClient.post('public/resume/jd-eval/export-pdf', { content }, {
      responseType: 'blob',
    });
    const blob = response.data;
    const disposition = response.headers['content-disposition'];
    let filename = `evaluation-${new Date().toISOString().slice(0, 10)}.pdf`;
    if (disposition && /filename[*]?=(.+)/i.test(disposition)) {
      const match = disposition.match(/filename[*]?=(?:UTF-8'')?["']?([^"'\s]+)["']?/i);
      if (match) filename = match[1].trim();
    }
    return { blob, filename };
  } catch (error) {
    if (error.response?.status === 400) throw new Error('Content cannot be empty.');
    if (error.request) throw new Error('Unable to connect to server.');
    throw new Error(error.message || 'Export failed.');
  }
};
